import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useContext } from 'react';

import { BrandContext, HOME_CHEF } from 'context/BrandContext';
import { getEnvConstants } from 'helpers/envConstants';
import Karla from 'Karla.ttf';

const { STRIPE_PUBLIC_KEY } = getEnvConstants();
const stripePromise = loadStripe(STRIPE_PUBLIC_KEY);

const KARLA_URL = 'https://asset.homechef.com/fonts/Karla.ttf';

/**
 *
 * Provides access to Stripe element components and the Stripe object to any
 * child component.
 * Read more: https://stripe.com/docs/stripe-js/react#elements-provider
 *
 */

const StripeElements = ({ children, options = {}, variant }) => {
  const { brand } = useContext(BrandContext);
  const isRefreshed = variant === 'refreshed';

  const selectedTabColor = brand === HOME_CHEF ? '#008600' : '#1071ea';
  const inputBorderColor = brand === HOME_CHEF ? '#F4E9E0' : '#D8DADC';

  const refreshedRules = {
    '.Input': {
      borderColor: inputBorderColor,
      borderRadius: '16px',
      boxShadow: 'none',
      padding: '16px 24px',
      lineHeight: '20px',
    },
    '.Tab': {
      borderRadius: '16px',
    },
    '.Tab:hover': {
      borderColor: selectedTabColor,
      color: selectedTabColor,
    },
    '.Tab:focus': {
      borderColor: selectedTabColor,
      boxShadow: `0 0 0 1px ${selectedTabColor}`,
      color: selectedTabColor,
    },
    '.Tab--selected': {
      borderColor: selectedTabColor,
      boxShadow: `0 0 0 1px ${selectedTabColor}`,
      color: selectedTabColor,
    },
    '.Tab--selected:hover': {
      borderColor: selectedTabColor,
      color: selectedTabColor,
    },
    '.Tab--selected:focus': {
      borderColor: selectedTabColor,
      boxShadow: `0 0 0 1px ${selectedTabColor}`,
      color: selectedTabColor,
    },
  };

  const defaultRules = {
    '.Input': {
      borderColor: '#8c9196',
    },
  };

  const defaultOptions = {
    appearance: {
      rules: {
        '.Label': {
          fontSize: '17px',
        },
        ...(isRefreshed ? refreshedRules : defaultRules),
      },
      variables: {
        fontFamily: brand === HOME_CHEF ? 'Karla, Helvetica Neue' : 'aktiv-grotesk, Helvetica Neue',
        gridRowSpacing: isRefreshed ? '16px' : '24px',
        buttonBorderRadius: '23px',
        ...(isRefreshed
          ? {
              tabIconColor: '#6D6E78',
              tabIconHoverColor: selectedTabColor,
              tabIconSelectedColor: selectedTabColor,
              colorPrimary: selectedTabColor,
            }
          : {}),
      },
    },
    paymentMethodCreation: 'manual',
    mode: 'setup',
    currency: 'usd',
    fonts: [
      {
        src: `url(${process.env.NODE_ENV === 'development' ? KARLA_URL : Karla})`,
        family: 'Karla',
        weight: '400',
      },
    ],
  };

  const mergedOptions = { ...defaultOptions, ...options };

  return (
    <Elements stripe={stripePromise} options={mergedOptions}>
      {children}
    </Elements>
  );
};

StripeElements.propTypes = {
  children: PropTypes.node.isRequired,
  options: PropTypes.object,
  variant: PropTypes.oneOf(['refreshed']),
};

export default StripeElements;
